/**
 * PersonalStepsSection.tsx
 * ─────────────────────────────────────────────────────────────────────────────
 * [배경] B2C 개인고객 전용 4단계 안내 섹션
 * 기존 StepsSection의 4단계를 개인고객 관점 문구로 변경하고,
 * 타임라인 UI + 원형 번호 뱃지 + 점선 연결선 디자인 적용.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import React from 'react';
import { Box, Typography, Grid, useTheme, useMediaQuery } from '@mui/material';
import {
  PhoneAndroid, HowToReg, LocalShipping, AccountBalance,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

interface StepItem {
  number: number;
  icon: React.ReactNode;
  title: string;
  description: string;
  gradient: string;
  shadowColor: string;
}

const steps: StepItem[] = [
  {
    number: 1,
    icon: <PhoneAndroid sx={{ fontSize: 26, color: '#fff' }} />,
    title: '기기 선택',
    description: '모델과 상태를 선택하면\n예상 금액을 바로 확인!',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    shadowColor: 'rgba(102, 126, 234, 0.3)',
  },
  {
    number: 2,
    icon: <HowToReg sx={{ fontSize: 26, color: '#fff' }} />,
    title: '본인 인증',
    description: '본인인증 한 번이면 끝\n회원가입 필요 없어요',
    gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    shadowColor: 'rgba(240, 147, 251, 0.3)',
  },
  {
    number: 3,
    icon: <LocalShipping sx={{ fontSize: 26, color: '#fff' }} />,
    title: '택배 수거',
    description: '원하는 시간에 택배 기사가\n직접 방문하여 수거',
    gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    shadowColor: 'rgba(79, 172, 254, 0.3)',
  },
  {
    number: 4,
    icon: <AccountBalance sx={{ fontSize: 26, color: '#fff' }} />,
    title: '검수 후 입금',
    description: '전문가 검수 후 금액 안내\n확인 즉시 계좌 입금',
    gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    shadowColor: 'rgba(67, 233, 123, 0.3)',
  },
];

const PersonalStepsSection: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box id="steps-section" sx={{ py: { xs: 4, md: 12 }, background: '#fff' }}>
      <Box sx={{ maxWidth: { xs: '100%', md: '50%' }, mx: 'auto', px: { xs: 2, md: 3 } }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Typography
            variant="h3"
            align="center"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '1.2rem', md: '2.2rem' },
              color: '#0f172a',
              mb: 1.5,
              letterSpacing: '-0.02em',
            }}
          >
            누구나 쉬운 4단계
          </Typography>
          <Typography
            variant="body1"
            align="center"
            sx={{
              color: '#64748b',
              mb: { xs: 2.5, md: 7 },
              fontSize: { xs: '0.85rem', md: '1.05rem' },
            }}
          >
            복잡한 절차 없이, 스마트폰 하나로 완료하세요
          </Typography>
        </motion.div>

        <Grid container spacing={{ xs: 2, md: 2 }}>
          {steps.map((step, index) => (
            <Grid item xs={6} md={3} key={step.number}>
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Box
                  sx={{
                    textAlign: 'center',
                    position: 'relative',
                    px: { xs: 2, md: 1 },
                  }}
                >
                  {/* 점선 연결선 (데스크톱, 마지막 스텝 제외) */}
                  {!isMobile && index < steps.length - 1 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 32,
                        left: '60%',
                        width: '80%',
                        height: 0,
                        borderTop: '2px dashed rgba(14, 165, 233, 0.2)',
                        zIndex: 0,
                      }}
                    />
                  )}

                  {/* 번호 뱃지 + 아이콘 */}
                  <Box sx={{ position: 'relative', display: 'inline-flex', mb: { xs: 1.5, md: 2.5 } }}>
                    <motion.div whileHover={{ scale: 1.1 }} transition={{ duration: 0.3 }}>
                      <Box
                        sx={{
                          width: { xs: 48, md: 64 },
                          height: { xs: 48, md: 64 },
                          borderRadius: '50%',
                          background: step.gradient,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: `0 8px 24px ${step.shadowColor}`,
                          position: 'relative',
                        }}
                      >
                        {step.icon}
                      </Box>
                    </motion.div>
                    {/* 번호 뱃지 */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -6,
                        right: -6,
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        background: '#0f172a',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 12,
                        fontWeight: 800,
                        border: '2px solid #fff',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      }}
                    >
                      {step.number}
                    </Box>
                  </Box>

                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: '#0f172a',
                      mb: 1,
                      fontSize: { xs: '0.9rem', md: '1.1rem' },
                    }}
                  >
                    {step.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#64748b',
                      lineHeight: 1.7,
                      whiteSpace: 'pre-line',
                      fontSize: { xs: '0.73rem', md: '0.85rem' },
                      mx: 'auto',
                    }}
                  >
                    {step.description}
                  </Typography>
                </Box>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default PersonalStepsSection;
